/**
 * useExchangeMagicCode Hook
 *
 * Exchanges a magic code for auth tokens using URQL mutation.
 */

import { useCallback } from "react";
import { useMutation } from "urql";
import { errorTracking } from "@/lib/error-tracking";

// =============================================================================
// MUTATION DOCUMENT
// =============================================================================

const ExchangeMagicCodeDocument = `
  mutation ExchangeMagicCode($email: String!, $code: String!) {
    exchangeMagicCode(email: $email, code: $code) {
      account {
        id
        telegramId
        firstName
        lastName
        username
        email
        hasPassword
      }
      tokens {
        accessToken
        refreshToken
        expiresAt
      }
    }
  }
`;

// =============================================================================
// TYPES
// =============================================================================

interface AuthAccount {
  id: string;
  telegramId: string | null;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  email: string | null;
  hasPassword: boolean;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  expiresAt: string;
}

interface ExchangeCodeResponse {
  account: AuthAccount;
  tokens: AuthTokens;
}

interface UseExchangeMagicCodeResult {
  exchangeMagicCode: (email: string, code: string) => Promise<ExchangeCodeResponse>;
  isLoading: boolean;
  error: Error | null;
}

// =============================================================================
// HOOK
// =============================================================================

/**
 * Exchange a magic code for auth tokens.
 */
export function useExchangeMagicCode(): UseExchangeMagicCodeResult {
  const [result, executeMutation] = useMutation<
    { exchangeMagicCode: ExchangeCodeResponse },
    { email: string; code: string }
  >(ExchangeMagicCodeDocument);

  const exchangeMagicCode = useCallback(
    async (email: string, code: string): Promise<ExchangeCodeResponse> => {
      try {
        const response = await executeMutation({
          email: email.toLowerCase().trim(),
          code,
        });

        if (response.error) {
          throw new Error(
            response.error.graphQLErrors[0]?.message ||
              response.error.message ||
              "Failed to exchange code"
          );
        }

        if (!response.data?.exchangeMagicCode) {
          throw new Error("No response from server");
        }

        errorTracking.addBreadcrumb({
          category: "auth",
          message: "Magic code exchanged",
        });

        return response.data.exchangeMagicCode;
      } catch (err) {
        const exchangeError =
          err instanceof Error ? err : new Error("Failed to exchange code");

        errorTracking.captureError(exchangeError, {
          tags: { feature: "auth", action: "exchangeMagicCode" },
        });

        throw exchangeError;
      }
    },
    [executeMutation]
  );

  return {
    exchangeMagicCode,
    isLoading: result.fetching,
    error: result.error ?? null,
  };
}
